import * as React from "react";
import { ActionParameterLabelSource, getActionParameterLabel, getParameterTypeTone } from "./action-parameter-label";
import { ActionParameterDocHint } from "./action-picker-popup/action-doc-hint";

export interface IActionParameterTitleProps {
  /** Only `displayName`/`name`, `Typename` and `cardinality` are read. A symbols group that names a
   *  parameter itself ("Step Key") passes that name as `name`. */
  parameter: ActionParameterLabelSource;
  /** Off where the parameter is a select (its options already say what it holds). */
  showTypeChips?: boolean;
  /** The parameter's documentation (`getActionParameterDoc`); an info glyph with a tooltip when set. */
  doc?: string;
}

/**
 * An action parameter's title: its name, then a type chip in that type's fixed colour
 * (`getParameterTypeTone`) and, when it is not the single default, a cardinality chip. Every
 * parameter label in the actions panel goes through here, so the generic parameter rows and the
 * special symbols groups (Notify, Send Mail, Navigation, …) read the same; they used to spell
 * `name (Type - Cardinality)` out by hand. When a `doc` is passed, an info glyph sits between the name
 * and the chips, as in the action picker's preview — only the generic parameter rows pass one today;
 * the special symbols groups call this without `doc`, so their titles carry no glyph.
 */
const ActionParameterTitle: React.FC<IActionParameterTitleProps> = ({
  parameter,
  showTypeChips = true,
  doc
}: IActionParameterTitleProps) => {
  const { name, typeName, cardinalityLabel } = getActionParameterLabel(parameter);
  return (
    <span className="kuika-action-section__param-name">
      {name}
      <ActionParameterDocHint doc={doc} parameterLabel={name} />
      {showTypeChips && typeName !== "" && (
        <span className="kuika-action-section__param-chips">
          <span
            className={`kuika-action-section__param-chip kuika-action-section__param-chip--${getParameterTypeTone(
              typeName
            )}`}
          >
            {typeName}
          </span>
          {cardinalityLabel && <span className="kuika-action-section__param-chip">{cardinalityLabel}</span>}
        </span>
      )}
    </span>
  );
};

export default ActionParameterTitle;
